/**
 * BreakdownGroupSection (FLOW-694)
 *
 * One group of a proposal: its epic header and the stories under it. Extracted
 * from `BreakdownPanel` when a breakdown stopped being one epic and became a
 * list of groups — the panel keeps the summary, the totals and the actions,
 * and repeats this once per group.
 *
 * Three framings, decided by the placement (FLOW-617). A `new` epic is itself
 * a proposal, so it reads as a heading with the stories beneath it. An
 * `existing` epic is context the user already owns and commit does not touch,
 * so it reads as "these stories are being added to it" — named by display id,
 * with the list visibly nested under that parent. A `none` group has no epic
 * to name and its stories simply stand on their own.
 */

import { Component, For, Show } from "solid-js";
import type { ProposedGroup } from "../../api/types";
import { ProposedStoryCard } from "./ProposedStoryCard";

export interface BreakdownGroupSectionProps {
  group: ProposedGroup;
  /** Position in the proposal — only used to keep element ids unique */
  index: number;
  rejectedStoryIds: Set<string>;
  onToggleStory: (id: string) => void;
}

export const BreakdownGroupSection: Component<BreakdownGroupSectionProps> = (
  props,
) => {
  const newEpic = () =>
    "new" in props.group.epic ? props.group.epic.new : undefined;
  const existingEpic = () =>
    "existing" in props.group.epic ? props.group.epic.existing : undefined;

  // Per group, not per panel: a shared id would point every list's
  // `aria-labelledby` at whichever heading rendered first.
  const labelId = () => `breakdown-story-list-label-${props.index}`;

  return (
    <div class="flex flex-col gap-4">
      <Show when={newEpic()}>
        {(epic) => (
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-ink-3">
              New epic
            </p>
            <h2 class="mt-1 text-lg font-semibold text-ink">{epic().title}</h2>
            <p class="mt-1 text-sm text-ink-2">{epic().description}</p>
          </div>
        )}
      </Show>

      <Show when={existingEpic()}>
        {(epic) => (
          <div class="rounded-md border border-line bg-sunken p-4">
            <p class="text-xs font-semibold uppercase tracking-wide text-ink-3">
              Adding to existing epic
            </p>
            <div class="mt-1 flex flex-wrap items-baseline gap-2">
              <span class="font-mono text-xs font-medium text-ink-3">
                {epic().display_id}
              </span>
              <h2 class="text-lg font-semibold text-ink">{epic().title}</h2>
            </div>
            <p class="mt-1 text-sm text-ink-2">
              Approved stories are added as children of this epic. The epic
              itself is unchanged.
            </p>
          </div>
        )}
      </Show>

      <div>
        <Show when={existingEpic()}>
          {(epic) => (
            <p
              id={labelId()}
              class="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-3"
            >
              Stories to add to {epic().display_id}
            </p>
          )}
        </Show>
        <Show when={!newEpic() && !existingEpic()}>
          <p
            id={labelId()}
            class="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-3"
          >
            Standalone stories
          </p>
        </Show>
        <ul
          aria-labelledby={newEpic() ? undefined : labelId()}
          class={
            "flex flex-col gap-4" +
            (existingEpic() ? " border-l-2 border-line pl-4" : "")
          }
        >
          <For each={props.group.stories}>
            {(story) => (
              <li>
                <ProposedStoryCard
                  story={story}
                  rejected={props.rejectedStoryIds.has(story.id)}
                  onToggle={props.onToggleStory}
                />
              </li>
            )}
          </For>
        </ul>
      </div>
    </div>
  );
};

export default BreakdownGroupSection;
